import { Trans } from "@lingui/macro"
import { useEffect, useState } from "react"
import { My } from "../../services/My"
import NotificationModal from "../main/NotificationModal"
import Page from "../../const/Page"
import { MemoModalEmitter } from "../rooms/right-menu/PanelMemos"
import hotkeys from "hotkeys-js"
import { useHistory } from "../../hooks/useRouterCompat";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu"
import { UserAvatar } from "./UserAvatar"
import { Util } from "../../services/Utils/Util"
import { MyProfileModal } from "./MyProfileModal"
import { TermsModal } from "./TermsModal"
import { AccountBlockingModal } from "./AccountBlockingModal"

let deferredPrompt: any;
const handler = (e: any) => {
  e.preventDefault();
  deferredPrompt = e;
};

window.addEventListener('beforeinstallprompt', handler);

export const ProfilePart: React.FC = () => {
  const [isPWAEnabled, setIsPWAEnabled] = useState(false);
  const [isNative, setIsNative] = useState(false);
  const [canInstallPWA, setCanInstallPWA] = useState<boolean>(Boolean(deferredPrompt));

  // Modal states
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isBlockingModalOpen, setIsBlockingModalOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    import('../../pwa-push').then(mod => {
      if (mounted) {
        // 네이티브 앱 체크 먼저
        const native = mod.isNativeApp();
        setIsNative(native);
        // 네이티브가 아닐 때만 PWA 체크
        if (!native) {
          setIsPWAEnabled(mod.isPWA());
        }
      }
    }).catch(() => {
      setIsPWAEnabled(false);
      setIsNative(false);
    });
    return () => { mounted = false; };
  }, []);

  const [open, setOpen] = useState(false);
  const history = useHistory();

  // 설치 가능 상태를 beforeinstallprompt 이벤트로 감지 (Chrome 계열에서만 발생)
  useEffect(() => {
    const onBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      deferredPrompt = e;
      setCanInstallPWA(true);
    };
    const onAppInstalled = () => {
      // 설치 완료 시 버튼 숨김 처리
      setCanInstallPWA(false);
      deferredPrompt = null;
      setIsPWAEnabled(true);
    };
    // 초기값 동기화 (혹시 모듈 스코프 리스너가 먼저 잡았을 경우)
    if (deferredPrompt) setCanInstallPWA(true);
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  useEffect(() => {
    hotkeys('shift+s', (e, handler) => {
      e.preventDefault();
      history.push(Page.my_soribots);
    })
    return () => { hotkeys.unbind('shift+s') };
  }, []);

  return (
    <div className='flex items-center justify-between w-full gap-2'>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <div className="flex items-center p-2 rounded-[14px] hover:bg-gray-100 cursor-pointer transition-colors select-none outline-none group min-w-0 flex-1" data-cy="profile">
            <UserAvatar
              src={My.user.photoUrl || Util.ANONYMOUS_PHOTO_URL}
              name={My.user.name}
              className="w-[50px] h-[50px] flex-shrink-0"
            />
            <div className="ml-2 flex flex-col min-w-0 text-left">
              <div className="text-sm font-bold text-[#111] truncate group-hover:text-gray-900">{My.user.name}</div>
              <div className="text-[13px] font-medium text-[#555] mt-1 truncate group-hover:text-gray-700">
                {My.user.email == "yoonsun@jiran.com" ? "test@nextpod.jp" : My.user.email}
              </div>
            </div>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="" align="start" sideOffset={0}>
          <DropdownMenuItem onClick={() => { setOpen(false); setIsProfileModalOpen(true); }} data-cy="profile" className="cursor-pointer">
            <Trans>My profile</Trans>
          </DropdownMenuItem>

          {!isNative && !isPWAEnabled && canInstallPWA && (<>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={askInstallPWA} className="desktop-only cursor-pointer">
              <Trans>Install NEXTPOD desktop</Trans>
            </DropdownMenuItem>
          </>)}

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => { setOpen(false); setIsTermsModalOpen(true); }} data-cy="terms" className="cursor-pointer">
            <Trans>Terms of service</Trans>
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => { setOpen(false); setIsBlockingModalOpen(true); }} className="cursor-pointer">
            <Trans>Account blocking</Trans>
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logoutButton} data-cy="logout" className="cursor-pointer text-red-600 focus:text-red-600">
            <Trans>Logout</Trans>
          </DropdownMenuItem>

          {getAdminButton()}
        </DropdownMenuContent>
      </DropdownMenu>
      <NotificationModal />

      {/* Modals */}
      <MyProfileModal open={isProfileModalOpen} onOpenChange={setIsProfileModalOpen} />
      <TermsModal open={isTermsModalOpen} onOpenChange={setIsTermsModalOpen} />
      <AccountBlockingModal open={isBlockingModalOpen} onOpenChange={setIsBlockingModalOpen} />
    </div>
  )

  function gotoDocSite() {
    window.open('https://sori.gitbook.io/sorichat', '_blank');
  }

  function helpText(text: string) {
    return <small className="ml-auto text-xs text-gray-400">{text}</small>
  }

  function openTextAssistant() {
    MemoModalEmitter.emit('text-assistant', ' ');
    setOpen(false);
  }

  async function askInstallPWA() {
    // deferredPrompt is a global variable we've been using in the sample to capture the `beforeinstallevent`
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    // Find out whether the user confirmed the installation or not
    const { outcome } = await deferredPrompt.userChoice;
    // The deferredPrompt can only be used once.
    deferredPrompt = null;
    // Act on the user's choice
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt.');
    } else if (outcome === 'dismissed') {
      console.log('User dismissed the install prompt');
    }
  }

  async function logoutButton() {
    const root = document.getElementById('root') || document.body;
    root.style.transition = 'opacity 0.3s ease-in-out';
    root.style.opacity = '0';

    setTimeout(async () => {
      await My.logoutUser();
      history.push('/login');
    }, 300);
  }

  function getAdminButton() {
    if (My.user.email.includes('@sunbisoft.com'))
      return (<>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Admin Pages</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => history.push('/admin_flagged')} className="cursor-pointer">
          <span><Trans>Flagged content</Trans></span>
        </DropdownMenuItem>
      </>)
    else
      return null
  }

}
